import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Globe,
  LayoutGrid,
  List,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Search,
  Store,
} from "lucide-react";
import { sellerService } from "@b2b/services";
import {
  filterPromotionCouncilFilterOptions,
  SELLER_BUSINESS_TYPE_OPTIONS,
} from "@b2b/ui-components";
import { BizcoverIndiaFigmaHeader } from "../../components/marketing/BizcoverIndiaFigmaHeader";
import { BizcoverIndiaFigmaFooter } from "../../components/marketing/BizcoverIndiaFigmaFooter";
import { useAuth } from "../../auth/Authprovider";
import { buildLoginReturnState } from "../../auth/hooks/usePostLoginRedirect";
import { getSellerDetailPath } from "@b2b/ui-components";

type ViewMode = "grid" | "list";
type SortByValue =
  | "createdAt"
  | "name"
  | "company"
  | "membershipPlan"
  | "verified"
  | "featured";
interface CategoryHierarchy {
  id: string;
  name: string;
}

// ── Page-singleton TTL caches (mirror of NewProductSearch caching) ─────
const SELLERS_CACHE_TTL_MS = 60_000;
const SELLERS_META_TTL_MS = 5 * 60_000;
const SELLERS_CACHE_MAX_ENTRIES = 50;
const SELLERS_STORAGE_KEY = "bz_sellers_cache_v2";

type SellersCacheEntry = {
  sellers: any[];
  pagination: any | null;
  expiresAt: number;
};

type SellersFilterOptions = { countries: string[]; councils: string[] };

const sellersCache = new Map<string, SellersCacheEntry>();
let sellersCategoriesCache: { data: CategoryHierarchy[]; expiresAt: number } | null = null;
let sellersFilterOptionsCache: { data: SellersFilterOptions; expiresAt: number } | null = null;

(function hydrateSellersCacheFromStorage() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    const raw = window.sessionStorage.getItem(SELLERS_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as {
      s?: Record<string, SellersCacheEntry>;
      c?: { data: CategoryHierarchy[]; expiresAt: number } | null;
      f?: { data: SellersFilterOptions; expiresAt: number } | null;
    };
    const now = Date.now();
    if (parsed?.s && typeof parsed.s === "object") {
      for (const [k, v] of Object.entries(parsed.s)) {
        if (v && typeof v.expiresAt === "number" && v.expiresAt > now) {
          sellersCache.set(k, v);
        }
      }
    }
    if (parsed?.c && typeof parsed.c.expiresAt === "number" && parsed.c.expiresAt > now) {
      sellersCategoriesCache = parsed.c;
    }
    if (parsed?.f && typeof parsed.f.expiresAt === "number" && parsed.f.expiresAt > now) {
      sellersFilterOptionsCache = parsed.f;
    }
  } catch {
    /* ignore — bad JSON, quota, or sessionStorage disabled */
  }
})();

function persistSellersCacheToStorage(): void {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    const payload = {
      s: Object.fromEntries(sellersCache),
      c: sellersCategoriesCache,
      f: sellersFilterOptionsCache,
    };
    window.sessionStorage.setItem(SELLERS_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota or serialization failure — drop silently */
  }
}

interface SellersCacheKeyInput {
  mode: "sellers" | "promotion";
  page: number;
  limit: number;
  search: string;
  categoryIdsQuery: string;
  businessTypesQuery: string;
  membershipPlansQuery: string;
  councilsQuery: string;
  sortBy: string;
  sortOrder: string;
}

function buildSellersCacheKey(input: SellersCacheKeyInput): string {
  // Field order is fixed so the JSON shape is stable across renders.
  return JSON.stringify([
    input.mode,
    input.page,
    input.limit,
    input.search,
    input.categoryIdsQuery,
    input.businessTypesQuery,
    input.membershipPlansQuery,
    input.councilsQuery,
    input.sortBy,
    input.sortOrder,
  ]);
}

function readSellersCache(key: string): SellersCacheEntry | null {
  const entry = sellersCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    sellersCache.delete(key);
    return null;
  }
  return entry;
}

function writeSellersCache(
  key: string,
  entry: Omit<SellersCacheEntry, "expiresAt">
): void {
  sellersCache.set(key, {
    ...entry,
    expiresAt: Date.now() + SELLERS_CACHE_TTL_MS,
  });
  while (sellersCache.size > SELLERS_CACHE_MAX_ENTRIES) {
    const oldest = sellersCache.keys().next().value;
    if (!oldest) break;
    sellersCache.delete(oldest);
  }
  persistSellersCacheToStorage();
}

function readSellersCategoriesCache(): CategoryHierarchy[] | null {
  if (!sellersCategoriesCache) return null;
  if (Date.now() > sellersCategoriesCache.expiresAt) {
    sellersCategoriesCache = null;
    return null;
  }
  return sellersCategoriesCache.data;
}

function writeSellersCategoriesCache(data: CategoryHierarchy[]): void {
  sellersCategoriesCache = {
    data,
    expiresAt: Date.now() + SELLERS_META_TTL_MS,
  };
  persistSellersCacheToStorage();
}

function readSellersFilterOptionsCache(): SellersFilterOptions | null {
  if (!sellersFilterOptionsCache) return null;
  if (Date.now() > sellersFilterOptionsCache.expiresAt) {
    sellersFilterOptionsCache = null;
    return null;
  }
  return sellersFilterOptionsCache.data;
}

function writeSellersFilterOptionsCache(data: SellersFilterOptions): void {
  sellersFilterOptionsCache = {
    data,
    expiresAt: Date.now() + SELLERS_META_TTL_MS,
  };
  persistSellersCacheToStorage();
}

const PLAN_FILTER_OPTIONS = ["Platinum", "Gold", "Silver"] as const;

/** Aligns with membership_plans.sortOrder: Platinum (3) > Gold (2) > Silver (1) > Free/others (0). */
function membershipPlanSortRank(planLabel: string, seller?: any): number {
  const fromApi = seller?.membershipPlanRank;
  // Ignore zero ranks from API when catalog sortOrder is unset in DB.
  if (typeof fromApi === "number" && Number.isFinite(fromApi) && fromApi > 0) {
    return fromApi;
  }
  const n = String(planLabel || "").trim().toLowerCase();
  if (!n || n === "n/a") return 0;
  if (n === "platinum" || n.includes("platinum")) return 3;
  if (n === "gold" || n.includes("gold")) return 2;
  if (n === "silver" || n.includes("silver")) return 1;
  if (n.includes("free")) return 0;
  return 0;
}

function compareMembershipPlanRanks(
  rankA: number,
  rankB: number,
  sortOrder: "asc" | "desc",
): number {
  if (rankA !== rankB) {
    return sortOrder === "asc" ? rankA - rankB : rankB - rankA;
  }
  return 0;
}

function sortSellersByMembershipPlan<T extends { id?: string }>(
  list: T[],
  getPlan: (seller: T) => string,
  sortOrder: "asc" | "desc",
): T[] {
  if (list.length <= 1) return [...list];
  return [...list].sort((a, b) => {
    const rankA = membershipPlanSortRank(getPlan(a), a);
    const rankB = membershipPlanSortRank(getPlan(b), b);
    const rankCmp = compareMembershipPlanRanks(rankA, rankB, sortOrder);
    if (rankCmp !== 0) return rankCmp;
    return String((a as any).company?.name || "").localeCompare(
      String((b as any).company?.name || ""),
    );
  });
}

function getMembershipPlanValue(seller: any): string {
  const fromTop = seller?.membershipPlanName;
  if (fromTop && String(fromTop).trim()) {
    return String(fromTop).trim();
  }
  const subs = seller?.company?.subscriptions;
  const activeSub = Array.isArray(subs)
    ? subs.find(
        (s: any) => String(s?.status || "").toUpperCase() === "ACTIVE",
      )
    : null;
  const sub = activeSub || subs?.[0] || seller?.company?.subscription;
  const raw =
    sub?.plan?.name ||
    sub?.planName ||
    seller?.membershipPlan ||
    seller?.planName ||
    seller?.subscription?.plan?.name ||
    seller?.subscription?.planName;
  return String(raw || "").trim() || "N/A";
}

interface SellerSummary {
  id: string;
  companyName: string;
  logo: string;
  country: string;
  city: string;
  businessType: string;
  yearsInBusiness: number | null;
  membershipPlan: string;
  isVerified: boolean;
  isFeatured: boolean;
}

const PLAN_TO_BADGE: Record<string, { label: string; className: string }> = {
  platinum: { label: "Platinum Shop", className: "bg-[#7C3AED] text-white" },
  gold: { label: "Gold Shop", className: "bg-[#F59E0B] text-white" },
  silver: { label: "Silver Shop", className: "bg-[#94A3B8] text-white" },
};

function getYearsInBusiness(foundedYear: unknown): number | null {
  const y =
    typeof foundedYear === "number"
      ? foundedYear
      : parseInt(String(foundedYear ?? "").trim(), 10);
  if (!Number.isFinite(y) || y < 1800) return null;
  const current = new Date().getFullYear();
  if (y > current + 1) return null;
  const diff = current - y;
  return diff < 0 ? null : Math.max(1, diff);
}

function extractSellerSummary(seller: any, fallbackPlan: string): SellerSummary {
  const company = seller?.company || {};
  const companyName =
    company.name ||
    `${seller?.firstName || ""} ${seller?.lastName || ""}`.trim() ||
    "Company";
  const country = String(company.country || seller?.country || "").trim();
  const city = String(company.city || seller?.city || "").trim();
  const businessType = String(
    seller?.businessType || company.businessType || ""
  ).trim();
  const foundedYear = company.foundedYear ?? seller?.foundedYear;
  const yearsInBusiness = getYearsInBusiness(foundedYear);
  const logo = String(company.logo || "").trim();
  const membershipPlan = fallbackPlan && fallbackPlan !== "N/A" ? fallbackPlan : "";
  const isVerified =
    Boolean(seller?.isVerified) ||
    Boolean(seller?.verified) ||
    String(seller?.sellerStatus || "").toUpperCase() === "APPROVED";
  const isFeatured =
    Boolean(seller?.isFeatured) || Boolean(seller?.featured);
  return {
    id: String(seller?.id || ""),
    companyName,
    logo,
    country,
    city,
    businessType,
    yearsInBusiness,
    membershipPlan,
    isVerified,
    isFeatured,
  };
}

function PlanBadge({ plan }: { plan: string }) {
  const key = plan.toLowerCase();
  const cfg = PLAN_TO_BADGE[key];
  if (!cfg) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cfg.className}`}
    >
      {cfg.label}
    </span>
  );
}

function normalizeExternalUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed.replace(/^\/+/, "")}`;
}

function DirectoryInfoRow({
  label,
  children,
  layout = "stacked",
}: {
  label: string;
  children: ReactNode;
  layout?: "stacked" | "inline";
}) {
  const stacked = layout === "stacked";
  return (
    <div
      className={
        stacked
          ? "flex min-w-0 flex-col gap-0.5 border-b border-gray-100 py-2 last:border-0"
          : "grid min-w-0 grid-cols-1 gap-0.5 border-b border-gray-100 py-2 last:border-0 md:grid-cols-[minmax(7rem,8rem)_minmax(0,1fr)] md:items-start md:gap-3"
      }
    >
      <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="min-w-0 break-words text-[13px] leading-relaxed text-gray-900">
        {children}
      </dd>
    </div>
  );
}

function ExportDirectoryCard({
  seller,
  companyName,
  viewMode,
}: {
  seller: any;
  companyName: string;
  viewMode: ViewMode;
}) {
  const meta = seller?.promotionCouncilMeta ?? {};
  const pcAddress = String(meta.address ?? "").trim();
  const pcEmail = String(meta.email ?? "").trim();
  const pcPhone = String(meta.contactNumber ?? "").trim();
  const pcWebsite = String(meta.website ?? "").trim();
  const pcCouncil = String(meta.councilName ?? "").trim();
  const pcCategory = String(meta.councilCategory ?? "").trim();
  const regionLabel = String(
    seller?.country ?? seller?.company?.country ?? "",
  ).trim();
  const telHref = pcPhone ? `tel:${pcPhone.replace(/[^\d+]/g, "")}` : "";
  const websiteHref = pcWebsite ? normalizeExternalUrl(pcWebsite) : null;

  const hasAnyDetail =
    pcCouncil ||
    pcCategory ||
    regionLabel ||
    pcAddress ||
    pcEmail ||
    pcPhone ||
    websiteHref;

  const detailLayout = viewMode === "list" ? "inline" : "stacked";

  return (
    <article
      className={`flex h-full min-w-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white transition-all hover:border-[#F97316] hover:shadow-md ${
        viewMode === "list" ? "w-full" : ""
      }`}
      aria-label={`Export directory: ${companyName}`}
    >
      <div
        className={`flex min-w-0 flex-1 flex-col p-4 sm:p-5 ${
          viewMode === "list" ? "gap-4 md:flex-row md:items-start md:gap-6" : ""
        }`}
      >
        <div
          className={
            viewMode === "list"
              ? "min-w-0 shrink-0 md:w-[min(100%,16rem)] md:max-w-[16rem]"
              : "min-w-0"
          }
        >
          <h3 className="break-words text-[15px] font-semibold leading-snug text-[#1A2A3D] sm:text-[16px]">
            {companyName}
          </h3>
        </div>

        {hasAnyDetail ? (
          <dl
            className={`min-w-0 w-full ${
              viewMode === "list" ? "md:flex-1" : "mt-3"
            }`}
          >
            {pcCouncil ? (
              <DirectoryInfoRow label="Council" layout={detailLayout}>
                {pcCouncil}
              </DirectoryInfoRow>
            ) : null}
            {pcCategory ? (
              <DirectoryInfoRow label="Category" layout={detailLayout}>
                {pcCategory}
              </DirectoryInfoRow>
            ) : null}
            {regionLabel ? (
              <DirectoryInfoRow label="State / Region" layout={detailLayout}>
                {regionLabel}
              </DirectoryInfoRow>
            ) : null}
            {pcAddress ? (
              <DirectoryInfoRow label="Address" layout={detailLayout}>
                <span className="whitespace-pre-wrap break-words">{pcAddress}</span>
              </DirectoryInfoRow>
            ) : null}
            {pcEmail ? (
              <DirectoryInfoRow label="Email" layout={detailLayout}>
                <a
                  href={`mailto:${pcEmail}`}
                  className="break-all font-medium text-[#F97316] hover:underline"
                >
                  {pcEmail}
                </a>
              </DirectoryInfoRow>
            ) : null}
            {pcPhone ? (
              <DirectoryInfoRow label="Phone" layout={detailLayout}>
                <a
                  href={telHref}
                  className="break-words font-medium text-gray-900 hover:text-[#F97316] hover:underline"
                >
                  {pcPhone}
                </a>
              </DirectoryInfoRow>
            ) : null}
            {websiteHref ? (
              <DirectoryInfoRow label="Website" layout={detailLayout}>
                <a
                  href={websiteHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex max-w-full items-start gap-1.5 break-all font-medium text-[#F97316] hover:underline"
                >
                  <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="min-w-0 break-all">{pcWebsite}</span>
                </a>
              </DirectoryInfoRow>
            ) : null}
          </dl>
        ) : (
          <p
            className={`text-[12px] italic text-gray-500 ${
              viewMode === "list" ? "md:flex-1" : "mt-3"
            }`}
          >
            No contact details on file for this entry.
          </p>
        )}
      </div>
    </article>
  );
}

export default function BizcoverIndiaSellerListPage() {
  const navigate = useNavigate();
  const { isAuthenticated, userRole } = useAuth();
  // Synchronous cache lookup before useState so the very first render already
  // has data — Alibaba-style "instant revisit" with no spinner flicker.
  const initialSellersCacheKey = buildSellersCacheKey({
    mode: "sellers",
    page: 1,
    limit: 12,
    search: "",
    categoryIdsQuery: "",
    businessTypesQuery: "",
    membershipPlansQuery: "",
    councilsQuery: "",
    sortBy: "membershipPlan",
    sortOrder: "desc",
  });
  const initialSellersCached = readSellersCache(initialSellersCacheKey);

  const [sellerSearchQuery, setSellerSearchQuery] = useState("");
  const [headerSearchQuery, setHeaderSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sortBy, setSortBy] = useState<SortByValue>("membershipPlan");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [sellers, setSellers] = useState<any[]>(
    () => initialSellersCached?.sellers ?? []
  );
  const [pagination, setPagination] = useState<any>(
    () => initialSellersCached?.pagination ?? null
  );
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(() => !initialSellersCached);
  const [error, setError] = useState<string | null>(null);
  const [footerEmail, setFooterEmail] = useState("");
  const [categories, setCategories] = useState<CategoryHierarchy[]>(
    () => readSellersCategoriesCache() ?? []
  );
  const [filterOptions, setFilterOptions] = useState<SellersFilterOptions | null>(
    () => readSellersFilterOptionsCache()
  );
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedBusinessTypes, setSelectedBusinessTypes] = useState<string[]>([]);
  const [selectedMembershipPlans, setSelectedMembershipPlans] = useState<string[]>([]);
  const [selectedCouncils, setSelectedCouncils] = useState<string[]>([]);
  const [showPromotionCouncils, setShowPromotionCouncils] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [sidebarCategorySearch, setSidebarCategorySearch] = useState("");
  const [isCategoryExpanded, setIsCategoryExpanded] = useState(true);
  const [isBusinessTypeExpanded, setIsBusinessTypeExpanded] = useState(true);
  const [isMembershipPlanExpanded, setIsMembershipPlanExpanded] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(sellerSearchQuery.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [sellerSearchQuery]);

  const categoryIdsQuery = useMemo(
    () => (selectedCategoryIds.length ? [...selectedCategoryIds].sort().join(",") : ""),
    [selectedCategoryIds]
  );
  const businessTypesQuery = useMemo(
    () => (selectedBusinessTypes.length ? [...selectedBusinessTypes].sort().join("|") : ""),
    [selectedBusinessTypes]
  );
  const councilsQuery = useMemo(() => {
    const visible = filterPromotionCouncilFilterOptions(selectedCouncils);
    return visible.length ? [...visible].sort().join("|") : "";
  }, [selectedCouncils]);
  const membershipPlansQuery = useMemo(
    () => (selectedMembershipPlans.length ? [...selectedMembershipPlans].sort().join("|") : ""),
    [selectedMembershipPlans]
  );

  useEffect(() => {
    if (readSellersCategoriesCache()) {
      // Already hydrated from cache via lazy useState init.
      return;
    }
    let active = true;
    sellerService
      .getCategories(true)
      .then((data: any) => {
        if (!active) return;
        const arr = (Array.isArray(data) ? data : []) as CategoryHierarchy[];
        writeSellersCategoriesCache(arr);
        setCategories(arr);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (readSellersFilterOptionsCache()) {
      return;
    }
    let active = true;
    sellerService
      .getSellerFilterOptions()
      .then((data) => {
        if (!active) return;
        const next: SellersFilterOptions = {
          countries: data?.countries ?? [],
          councils: data?.councils ?? [],
        };
        writeSellersFilterOptionsCache(next);
        setFilterOptions(next);
      })
      .catch(() => {
        if (!active) return;
        setFilterOptions({ countries: [], councils: [] });
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const cacheKey = buildSellersCacheKey({
      mode: showPromotionCouncils ? "promotion" : "sellers",
      page,
      limit: 12,
      search: debouncedSearch,
      categoryIdsQuery: showPromotionCouncils ? "" : categoryIdsQuery,
      businessTypesQuery: showPromotionCouncils ? "" : businessTypesQuery,
      membershipPlansQuery: showPromotionCouncils ? "" : membershipPlansQuery,
      councilsQuery: showPromotionCouncils ? councilsQuery : "",
      sortBy,
      sortOrder,
    });

    const cached = readSellersCache(cacheKey);
    if (cached) {
      setSellers(cached.sellers);
      setPagination(cached.pagination);
      setError(null);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    const apiSortBy =
      sortBy === "verified" || sortBy === "featured" ? "membershipPlan" : sortBy;

    const req = showPromotionCouncils
      ? sellerService.getPromotionCouncilSellers({
          page,
          limit: 12,
          search: debouncedSearch || undefined,
          ...(councilsQuery ? { councils: councilsQuery } : {}),
          sortBy: apiSortBy,
          sortOrder,
        })
      : sellerService.getSellers({
          page,
          limit: 12,
          search: debouncedSearch || undefined,
          ...(categoryIdsQuery ? { categoryIds: categoryIdsQuery } : {}),
          ...(businessTypesQuery ? { businessTypes: businessTypesQuery } : {}),
          ...(membershipPlansQuery ? { membershipPlans: membershipPlansQuery } : {}),
          sortBy: apiSortBy,
          sortOrder,
        });

    req
      .then((data) => {
        if (!active) return;
        const list = Array.isArray(data?.sellers) ? data.sellers : [];
        const paginationData = data?.pagination || null;
        setSellers(list);
        setPagination(paginationData);
        setLoading(false);
        writeSellersCache(cacheKey, { sellers: list, pagination: paginationData });
      })
      .catch(() => {
        if (!active) return;
        setError("Failed to load shops");
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [debouncedSearch, sortBy, sortOrder, page, categoryIdsQuery, businessTypesQuery, membershipPlansQuery, showPromotionCouncils, councilsQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sortBy, sortOrder, selectedCategoryIds, selectedBusinessTypes, selectedMembershipPlans, selectedCouncils, showPromotionCouncils]);

  useEffect(() => {
    if (showPromotionCouncils) {
      setSelectedMembershipPlans([]);
    } else {
      setSelectedCouncils([]);
    }
  }, [showPromotionCouncils]);

  const displayedSellers = useMemo(() => {
    if (!Array.isArray(sellers)) return [];
    if (showPromotionCouncils || sortBy !== "membershipPlan") return sellers;
    return sortSellersByMembershipPlan(sellers, getMembershipPlanValue, sortOrder);
  }, [sellers, sortBy, sortOrder, showPromotionCouncils]);

  // Pre-compute card data once per sellers list so unrelated re-renders
  // (sidebar toggles, hover, debounce ticks) don't re-derive every card.
  const sellerSummaries = useMemo(
    () =>
      displayedSellers.map((seller) => ({
        seller,
        summary: extractSellerSummary(seller, getMembershipPlanValue(seller)),
      })),
    [displayedSellers]
  );

  const categoryOptions = useMemo(() => categories.map((c) => ({ id: c.id, name: c.name })), [categories]);
  const categoryFilterList = useMemo(() => {
    const base = showAllCategories ? categoryOptions : categoryOptions.slice(0, 8);
    const q = sidebarCategorySearch.trim().toLowerCase();
    if (!q) return base;
    return base.filter((c) => c.name.toLowerCase().includes(q));
  }, [categoryOptions, showAllCategories, sidebarCategorySearch]);

  const councilOptions = useMemo(() => {
    const uniq = new Map<string, string>();
    (filterOptions?.councils ?? []).forEach((item) => {
      const v = String(item || "").trim();
      if (!v) return;
      const k = v.toLowerCase();
      if (!uniq.has(k)) uniq.set(k, v);
    });
    return filterPromotionCouncilFilterOptions(
      Array.from(uniq.values()).sort((a, b) => a.localeCompare(b))
    );
  }, [filterOptions?.councils]);

  const showingText = useMemo(() => {
    const total = Number(pagination?.total || displayedSellers.length || 0);
    const currentPage = Number(pagination?.page || page || 1);
    const limit = Number(pagination?.limit || 12);
    if (total === 0) return "Showing 0 results";
    const start = (currentPage - 1) * limit + 1;
    const end = Math.min(currentPage * limit, total);
    return `Showing ${start}-${end} of ${total} results`;
  }, [pagination, displayedSellers.length, page]);

  const pageNumbers = useMemo(() => {
    const totalPages = Number(pagination?.totalPages || 1);
    const current = Number(pagination?.page || page || 1);
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(1, current - half);
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const nums: number[] = [];
    for (let i = start; i <= end; i += 1) nums.push(i);
    return { nums, totalPages, current };
  }, [pagination, page]);

  const openSeller = (
    seller?: string | { id?: string; company?: { slug?: string | null } | null },
  ) => {
    if (!seller) return;
    const targetPath = getSellerDetailPath(seller);
    if (!isAuthenticated) {
      navigate("/login", { state: buildLoginReturnState(targetPath) });
      return;
    }
    navigate(targetPath);
  };

  const toggleMultiSelect = (value: string, setter: any) => {
    setter((prev: string[]) => (prev.includes(value) ? prev.filter((v: string) => v !== value) : [...prev, value]));
  };

  const handleClearFilters = () => {
    setSelectedCategoryIds([]);
    setSelectedBusinessTypes([]);
    setSelectedMembershipPlans([]);
    setSelectedCouncils([]);
    setShowPromotionCouncils(false);
    setSidebarCategorySearch("");
  };

  return (
    <div className="min-h-screen bg-[#f4f6f8]">
      <BizcoverIndiaFigmaHeader
        searchQuery={headerSearchQuery}
        onSearchChange={setHeaderSearchQuery}
        onSearchSubmit={() => {}}
      />

      <main className="w-full px-4 py-6 lg:px-36">
        <div className="mb-3 flex items-center gap-2 text-[12px] text-[#919eab]">
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[#dfe3e8]">
            <Building2 className="h-3 w-3" />
          </span>
          <span>Home</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-[#637381]">Shops</span>
        </div>

        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-200 pb-3">
          <h1 className="text-[22px] font-semibold tracking-tight text-[#1A2A3D] sm:text-[24px]">
            Shops
          </h1>
          <span className="text-[12px] text-gray-500">{showingText}</span>
        </div>

        <section className="grid gap-4 lg:grid-cols-[260px_1fr]">
          <aside className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-5 self-start">
            <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3">
              <h3 className="text-[13px] font-semibold uppercase tracking-wide text-gray-700">
                Filters
              </h3>
              <button
                type="button"
                onClick={handleClearFilters}
                className="text-[11px] font-medium text-gray-500 hover:text-[#F97316]"
              >
                Clear all
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <button
                  type="button"
                  className="mb-2 flex w-full items-center justify-between"
                  onClick={() => setIsMembershipPlanExpanded((prev) => !prev)}
                >
                  <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-700">Plans</span>
                  <ChevronDown className={`h-4 w-4 text-[#6b7280] ${isMembershipPlanExpanded ? "" : "-rotate-90"}`} />
                </button>
                {isMembershipPlanExpanded && !showPromotionCouncils && (
                  <div className="space-y-1.5">
                    {PLAN_FILTER_OPTIONS.map((plan) => (
                      <label key={plan} className="flex items-center gap-2 text-xs text-[#374151]">
                        <input
                          type="checkbox"
                          checked={selectedMembershipPlans.includes(plan)}
                          onChange={() => toggleMultiSelect(plan, setSelectedMembershipPlans)}
                        />
                        <span>{plan}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <button
                  type="button"
                  className="mb-2 flex w-full items-center justify-between"
                  onClick={() => setIsCategoryExpanded((prev) => !prev)}
                >
                  <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-700">Industries</span>
                  <ChevronDown className={`h-4 w-4 text-[#6b7280] ${isCategoryExpanded ? "" : "-rotate-90"}`} />
                </button>
                {isCategoryExpanded && (
                  <>
                    {showAllCategories && (
                      <input
                        value={sidebarCategorySearch}
                        onChange={(e) => setSidebarCategorySearch(e.target.value)}
                        placeholder="Search industries"
                        className="mb-2 h-8 w-full rounded border border-[#e6ebf0] px-2 text-xs outline-none"
                      />
                    )}
                    <div className="max-h-[220px] space-y-1.5 overflow-auto pr-1">
                      {categoryFilterList.map((cat) => (
                        <label key={cat.id} className="flex items-center gap-2 text-xs text-[#374151]">
                          <input
                            type="checkbox"
                            checked={selectedCategoryIds.includes(cat.id)}
                            onChange={() => toggleMultiSelect(cat.id, setSelectedCategoryIds)}
                          />
                          <span>{cat.name}</span>
                        </label>
                      ))}
                    </div>
                    {categoryOptions.length > 8 && (
                      <button
                        type="button"
                        onClick={() => {
                          if (showAllCategories) setSidebarCategorySearch("");
                          setShowAllCategories((prev) => !prev);
                        }}
                        className="mt-2 text-xs font-semibold text-[#F97316]"
                      >
                        {showAllCategories ? "Show less" : "Show more"}
                      </button>
                    )}
                  </>
                )}
              </div>

              <div>
                <button
                  type="button"
                  className="mb-2 flex w-full items-center justify-between"
                  onClick={() => setIsBusinessTypeExpanded((prev) => !prev)}
                >
                  <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-700">Business Type</span>
                  <ChevronDown className={`h-4 w-4 text-[#6b7280] ${isBusinessTypeExpanded ? "" : "-rotate-90"}`} />
                </button>
                {isBusinessTypeExpanded && (
                  <div className="space-y-1.5">
                    {SELLER_BUSINESS_TYPE_OPTIONS.map((label) => (
                      <label key={label} className="flex items-center gap-2 text-xs text-[#374151]">
                        <input
                          type="checkbox"
                          checked={selectedBusinessTypes.includes(label)}
                          onChange={() => toggleMultiSelect(label, setSelectedBusinessTypes)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <label className="flex items-start gap-2 border-t border-[#eef1f4] pt-3 text-xs text-[#374151]">
                <input
                  type="checkbox"
                  checked={showPromotionCouncils}
                  onChange={(e) => setShowPromotionCouncils(e.target.checked)}
                />
                <span>
                  <span className="font-semibold text-[#111827]">Export Directory</span>
                </span>
              </label>

              {showPromotionCouncils && (
                <div className="space-y-1.5 border-t border-[#eef1f4] pt-3">
                  <p className="text-sm font-semibold text-[#111827]">Council</p>
                  <div className="max-h-[180px] space-y-1.5 overflow-auto pr-1">
                    {councilOptions.map((council) => (
                      <label key={council} className="flex items-center gap-2 text-xs text-[#374151]">
                        <input
                          type="checkbox"
                          checked={selectedCouncils.includes(council)}
                          onChange={() => toggleMultiSelect(council, setSelectedCouncils)}
                        />
                        <span>{council}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>

          <div className="min-w-0 rounded-lg bg-white p-4 shadow-sm sm:p-5">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex items-center gap-1 rounded-full border border-gray-200 p-1">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
                  viewMode === "grid"
                    ? "bg-[#F97316] text-white"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
                  viewMode === "list"
                    ? "bg-[#F97316] text-white"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>

            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              <label className="relative w-full sm:w-[240px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={sellerSearchQuery}
                  onChange={(e) => setSellerSearchQuery(e.target.value)}
                  placeholder="Search company"
                  className="h-9 w-full rounded-full border border-gray-200 bg-white pl-9 pr-3 text-[12px] text-gray-700 placeholder:text-gray-400 outline-none focus:border-[#F97316] focus:ring-2 focus:ring-[#F97316]/15"
                />
              </label>
              <span className="text-[12px] text-gray-500">Sort</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortByValue)}
                className="h-9 rounded-full border border-gray-200 bg-white px-3 text-[12px] text-gray-700 outline-none focus:ring-2 focus:ring-[#F97316]/15"
              >
                <option value="membershipPlan">Membership plan</option>
                <option value="createdAt">Newest</option>
                <option value="company">Company name</option>
                <option value="verified">Verified shop</option>
                <option value="featured">Featured shop</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
                className="h-9 rounded-full border border-gray-200 bg-white px-3 text-[12px] text-gray-700 outline-none focus:ring-2 focus:ring-[#F97316]/15"
              >
                <option value="desc">Desc</option>
                <option value="asc">Asc</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="py-10 text-center text-sm text-[#637381]">
              {showPromotionCouncils ? "Loading export directory..." : "Loading shops..."}
            </div>
          ) : error ? (
            <div className="py-10 text-center text-sm text-[#dc2626]">{error}</div>
          ) : displayedSellers.length === 0 ? (
            <div className="py-10 text-center text-sm text-[#637381]">
              {showPromotionCouncils
                ? "No export directory entries found."
                : "No shops found."}
            </div>
          ) : (
            <>
              <div
                className={
                  viewMode === "grid"
                    ? showPromotionCouncils
                      ? "grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-2"
                      : "grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
                    : "flex min-w-0 flex-col gap-3"
                }
              >
                {sellerSummaries.map(({ seller, summary }, index) => {
                  if (seller?.isPromotionCouncilEntry) {
                    return (
                      <ExportDirectoryCard
                        key={seller?.id || `export-${index}`}
                        seller={seller}
                        companyName={summary.companyName}
                        viewMode={viewMode}
                      />
                    );
                  }

                  const location = [summary.city, summary.country]
                    .filter(Boolean)
                    .join(", ");
                  const canMessage = isAuthenticated && userRole === "buyer";
                  const canVisitShop = true;
                  const onMessage = () => {
                    if (!summary.id) return;
                    navigate(`/chat?sellerId=${encodeURIComponent(summary.id)}`);
                  };
                  const onVisit = () => openSeller(summary);

                  if (viewMode === "list") {
                    return (
                      <article
                        key={seller?.id || `seller-${index}`}
                        className="group flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4 transition-all hover:border-[#F97316] hover:shadow-md sm:flex-row"
                      >
                        <button
                          type="button"
                          onClick={onVisit}
                          className="relative h-[120px] w-full flex-shrink-0 overflow-hidden rounded-md border border-gray-100 bg-gradient-to-b from-gray-50 to-gray-100 sm:w-[140px]"
                          aria-label={`Visit ${summary.companyName}`}
                        >
                          {summary.logo ? (
                            <img
                              src={summary.logo}
                              alt={summary.companyName}
                              className="h-full w-full object-contain p-3"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-gray-300">
                              <Building2 className="h-10 w-10" />
                            </div>
                          )}
                        </button>

                        <div className="flex min-w-0 flex-1 flex-col">
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={onVisit}
                              className="line-clamp-1 text-left text-[15px] font-semibold text-[#1A2A3D] transition-colors hover:text-[#F97316]"
                            >
                              {summary.companyName}
                            </button>
                            {summary.isVerified && (
                              <span className="inline-flex items-center gap-1 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                                <CheckCircle2 className="h-3 w-3" />
                                Verified
                              </span>
                            )}
                            <PlanBadge plan={summary.membershipPlan} />
                          </div>

                          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-gray-500">
                            {location && (
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {location}
                              </span>
                            )}
                            {summary.businessType && <span>{summary.businessType}</span>}
                            {summary.yearsInBusiness != null && (
                              <span>
                                {summary.yearsInBusiness}{" "}
                                {summary.yearsInBusiness === 1 ? "yr" : "yrs"}
                              </span>
                            )}
                          </div>

                        </div>

                        <div className="flex flex-shrink-0 items-end gap-2 sm:flex-col sm:items-stretch">
                          {canMessage && (
                            <button
                              type="button"
                              onClick={onMessage}
                              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-gray-200 px-4 text-[12px] font-semibold text-gray-700 transition-colors hover:border-[#F97316] hover:text-[#F97316]"
                            >
                              <MessageCircle className="h-3.5 w-3.5" />
                              Message
                            </button>
                          )}
                          {canVisitShop && (
                            <button
                              type="button"
                              onClick={onVisit}
                              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full bg-[#F97316] px-4 text-[12px] font-semibold text-white transition-colors hover:bg-[#EA580C]"
                            >
                              <Store className="h-3.5 w-3.5" />
                              Visit Shop
                            </button>
                          )}
                        </div>
                      </article>
                    );
                  }

                  return (
                    <article
                      key={seller?.id || `seller-${index}`}
                      className="group flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white transition-all hover:border-[#F97316] hover:shadow-md"
                    >
                      <button
                        type="button"
                        onClick={onVisit}
                        className="relative h-[140px] w-full overflow-hidden bg-gradient-to-b from-gray-50 to-gray-100"
                        aria-label={`Visit ${summary.companyName}`}
                      >
                        {summary.logo ? (
                          <img
                            src={summary.logo}
                            alt={summary.companyName}
                            className="h-full w-full object-contain p-4"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-gray-300">
                            <Building2 className="h-12 w-12" />
                          </div>
                        )}
                        {summary.isVerified && (
                          <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-sm bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow">
                            <CheckCircle2 className="h-3 w-3" />
                            Verified
                          </span>
                        )}
                      </button>

                      <div className="flex flex-1 flex-col p-4">
                        <button
                          type="button"
                          onClick={onVisit}
                          className="line-clamp-1 text-left text-[14px] font-semibold text-[#1A2A3D] transition-colors hover:text-[#F97316]"
                        >
                          {summary.companyName}
                        </button>

                        <div className="mt-2 min-h-[20px]">
                          <PlanBadge plan={summary.membershipPlan} />
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-gray-500">
                          {location && (
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {location}
                            </span>
                          )}
                          {summary.businessType && (
                            <span className="truncate">{summary.businessType}</span>
                          )}
                        </div>

                        {summary.yearsInBusiness != null && (
                          <p className="mt-2 text-[11px] text-gray-500">
                            Years in business:{" "}
                            <span className="font-semibold text-gray-700">
                              {summary.yearsInBusiness}
                            </span>
                          </p>
                        )}

                        <div
                          className={`mt-4 grid gap-2 ${
                            canMessage && canVisitShop
                              ? "grid-cols-2"
                              : canMessage || canVisitShop
                                ? "grid-cols-1"
                                : "hidden"
                          }`}
                        >
                          {canMessage && (
                            <button
                              type="button"
                              onClick={onMessage}
                              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-gray-200 px-3 text-[12px] font-semibold text-gray-700 transition-colors hover:border-[#F97316] hover:text-[#F97316]"
                            >
                              <MessageCircle className="h-3.5 w-3.5" />
                              Message
                            </button>
                          )}
                          {canVisitShop && (
                            <button
                              type="button"
                              onClick={onVisit}
                              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full bg-[#F97316] px-3 text-[12px] font-semibold text-white transition-colors hover:bg-[#EA580C]"
                            >
                              <Store className="h-3.5 w-3.5" />
                              Visit Shop
                            </button>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
              {pageNumbers.totalPages > 1 && (
                <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[#eef1f4] pt-4">
                  <p className="text-xs text-[#637381]">
                    Page <span className="font-semibold text-[#111827]">{pageNumbers.current}</span> of{" "}
                    <span className="font-semibold text-[#111827]">{pageNumbers.totalPages}</span>
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      disabled={pageNumbers.current <= 1}
                      className="inline-flex h-8 items-center gap-1 rounded-full border border-[#dfe3e8] bg-white px-3 text-xs font-semibold text-[#334155] disabled:opacity-50"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                      Prev
                    </button>
                    {pageNumbers.nums.map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setPage(n)}
                        className={`inline-flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-xs font-semibold ${
                          n === pageNumbers.current
                            ? "bg-[#F97316] text-white"
                            : "border border-[#dfe3e8] bg-white text-[#334155]"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setPage((prev) => Math.min(pageNumbers.totalPages, prev + 1))}
                      disabled={pageNumbers.current >= pageNumbers.totalPages}
                      className="inline-flex h-8 items-center gap-1 rounded-full border border-[#dfe3e8] bg-white px-3 text-xs font-semibold text-[#334155] disabled:opacity-50"
                    >
                      Next
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          </div>
        </section>
      </main>

      <BizcoverIndiaFigmaFooter
        email={footerEmail}
        onEmailChange={setFooterEmail}
        onSubscribe={() => {}}
      />
    </div>
  );
}
